import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCopy } from './copy/copyMode';
import { WaitlistCTA } from './WaitlistCTA';
import { trackPricingPlanSelected } from '../lib/analytics';
export type Tier = {
  credits: number;
  price: number;
  popular?: boolean;
};
export type Group = {
  group: string;
};
export type DropdownItem = Tier | Group;
// Exported so other surfaces (e.g. the dashboard billing modal) present the
// exact same tiers and groupings as the pricing page.
export const PRICING_DATA: DropdownItem[] = [{
  credits: 20000,
  price: 50
}, {
  credits: 30000,
  price: 75
}, {
  credits: 40000,
  price: 100
}, {
  credits: 80000,
  price: 200
}, {
  group: 'Small-sized companies'
}, {
  credits: 125000,
  price: 300
}, {
  credits: 160000,
  price: 400
}, {
  credits: 200000,
  price: 500
}, {
  credits: 300000,
  price: 750,
  popular: true
}, {
  credits: 400000,
  price: 1000
}, {
  credits: 600000,
  price: 1500
}, {
  credits: 800000,
  price: 2000
}, {
  credits: 1200000,
  price: 3000
}, {
  credits: 1600000,
  price: 4000
}, {
  credits: 2000000,
  price: 5000
}, {
  group: 'Medium-sized companies'
}, {
  credits: 3000000,
  price: 7500
}, {
  credits: 4000000,
  price: 10000
}, {
  credits: 5000000,
  price: 12500
}, {
  credits: 6000000,
  price: 15000
}, {
  credits: 8000000,
  price: 20000
}, {
  credits: 10000000,
  price: 25000
}, {
  credits: 12000000,
  price: 30000
}, {
  group: 'Enterprise'
}, {
  credits: 14000000,
  price: 35000
}, {
  credits: 16000000,
  price: 40000
}, {
  credits: 18000000,
  price: 45000
}, {
  credits: 20000000,
  price: 50000
}];
function FeatureItem({
  text,
  dark = false
}: {
  text: string;
  dark?: boolean;
}) {
  return <li className={`flex items-start gap-3 text-sm md:text-base ${dark ? 'text-white/90' : 'text-[#2B211A]'}`} data-id="element-3988" data-mp="k03bhpri8tz3">
      
      <div className={`mt-0.5 shrink-0 rounded-full p-1 ${dark ? 'bg-white/20 text-white' : 'bg-[#EA580C] text-white'}`} data-id="element-3989" data-mp="hhrs2va92tm3">
        
        <Check size={12} strokeWidth={3} data-id="element-3990" data-mp="mftcbjjewyz5" />
      </div>
      <span className="leading-relaxed font-medium" data-id="element-3991" data-mp="mei7rhi57n6z">{text}</span>
    </li>;
}
export function PricingSection() {
  const copy = useCopy();
  const [selectedTier, setSelectedTier] = useState<Tier>(PRICING_DATA[0] as Tier);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsDropdownOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  return <section className="relative w-full bg-[#FAF8F4] py-16 md:py-32 overflow-hidden" data-id="element-3992" data-mp="2sdbnca2v39gd">
      {/* Halftone dot-matrix texture */}
      <div aria-hidden="true" className="absolute inset-0 dot-matrix opacity-40 pointer-events-none z-0" data-id="element-3993" data-mp="369osqku4fkeh" />
      

      <div className="max-w-[1000px] mx-auto px-6 md:px-10 relative z-10" data-id="element-3994" data-mp="31f1d7bh3i0c1">
        {/* Header */}
        <div className="mb-12 md:mb-16 text-center" data-id="element-3995" data-mp="3g7mgl782jf6k">
          <h2 className="text-4xl md:text-6xl font-title font-bold text-[#2B211A] tracking-tight leading-[1.1]" data-id="element-3996" data-mp="3ba32c7bdf2em">
            {copy.pricingSection.headline}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-id="element-3997" data-mp="3l0fkxtzmkli4">
          {/* Team Card */}
          <div className="bg-white rounded-[2rem] p-8 md:p-10 shadow-[0_8px_40px_-12px_rgba(0,0,0,0.1)] border border-neutral-200 flex flex-col relative z-20" data-id="element-3998" data-mp="2w48bb7ylr3r">
            <div className="mb-8" data-id="element-3999" data-mp="hy2mz25qs71u">
              <span className="text-[#EA580C] font-semibold text-sm mb-4 block" data-id="element-4000" data-mp="hyvtc20sgujg">
                Team
              </span>
              <div className="flex items-baseline gap-1 mb-6" data-id="element-4001" data-mp="ly4onbbh90h4">
                <span className="text-5xl md:text-6xl font-title font-bold text-[#2B211A]" data-id="element-4002" data-mp="qpw9d57ycbzc">
                  ${selectedTier.price.toLocaleString()}
                </span>
                <span className="text-neutral-500 font-medium" data-id="element-4003" data-mp="3v4m82s0odhcj">/ month</span>
              </div>

              <div className="space-y-2 relative" ref={dropdownRef} data-id="element-4004" data-mp="3v6yvvlt46lxi">
                <label className="text-sm text-neutral-500 font-medium block" data-id="element-4005" data-mp="313ivoa3mwc4">
                  Shared workspace credits
                </label>
                <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} aria-haspopup="listbox" aria-expanded={isDropdownOpen} aria-label="Select monthly credit tier" className="w-full flex items-center justify-between px-5 py-4 bg-white border border-neutral-200 hover:border-neutral-300 rounded-xl text-left transition-colors focus:outline-none focus:ring-2 focus:ring-[#EA580C]/20" data-id="element-4006" data-mp="cyq38qa63g3h">
                  
                  <span className="font-medium text-[#2B211A] text-lg" data-id="element-4007" data-mp="33wkqjdkjcycl">
                    {selectedTier.credits.toLocaleString()} credits monthly
                  </span>
                  <ChevronDown size={20} className={`text-neutral-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} data-id="element-4008" data-mp="2u7aw3r27z3lb" />
                  
                </button>

                <AnimatePresence data-id="element-4009" data-mp="2q3cwlf8zfqrq">
                  {isDropdownOpen && <motion.div initial={{
                  opacity: 0,
                  y: -10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.15
                }} role="listbox" aria-label="Monthly credit tiers" className="absolute top-full left-0 right-0 mt-2 bg-white border border-neutral-200 rounded-2xl shadow-2xl max-h-[360px] overflow-y-auto z-50 p-2" data-id="element-4010" data-mp="2l5l34gsovwlf">
                    
                      {PRICING_DATA.map((item, idx) => {
                    if ('group' in item) {
                      return <div key={`group-${idx}`} className="px-4 pt-6 pb-2 text-[11px] font-bold tracking-wider text-neutral-400 uppercase" data-id="element-4011" data-mp="1tzmkkbvokh2c">
                            
                              {item.group}
                            </div>;
                    }
                    const isSelected = item.credits === selectedTier.credits;
                    return <button key={item.credits} role="option" aria-selected={isSelected} onClick={() => {
                      trackPricingPlanSelected(item.credits, item.price);
                      setSelectedTier(item);
                      setIsDropdownOpen(false);
                    }} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-left transition-colors ${isSelected ? 'bg-[#FCF3EC] text-[#EA580C]' : 'hover:bg-neutral-50 text-[#2B211A]'}`} data-id="element-4012" data-mp="2rm3fjagxyikx" data-mpi={idx}>
                          
                            <div className="flex items-center gap-3" data-id="element-4013" data-mp="yru589rlkhod">
                              <div className="w-5 flex justify-center" data-id="element-4014" data-mp="tqefi4eh5mzn">
                                {isSelected && <Check size={16} strokeWidth={3} data-id="element-4015" data-mp="18mvud5lvhjqy" />}
                              </div>
                              <span className="font-medium" data-id="element-4016" data-mp="13pn7wstoytjw">
                                {item.credits.toLocaleString()} credits monthly
                              </span>
                            </div>
                            <div className="flex items-center gap-3" data-id="element-4017" data-mp="b29jxk7z18bx">
                              {item.popular && <span className="bg-[#FBEAE0] text-[#EA580C] text-xs font-semibold px-2.5 py-1 rounded-full" data-id="element-4018" data-mp="pqvwje67rlzg">
                                  Most popular
                                </span>}
                              <span className={`font-medium ${isSelected ? 'text-[#EA580C]' : 'text-neutral-500'}`} data-id="element-4019" data-mp="3tcl6vbz3stgh">
                              
                                ${item.price.toLocaleString()}
                              </span>
                            </div>
                          </button>;
                  })}
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>

            <WaitlistCTA analyticsLabel="pricing-team" className="w-full flex justify-center bg-[#EA580C] hover:bg-[#C2410C] text-white py-4 rounded-xl font-bold transition-colors mb-10" data-id="element-4020" data-mp="3gvsuc1k20sdy">
              
              Get Started for Free
            </WaitlistCTA>

            <ul className="space-y-4 flex-1" data-id="element-4021" data-mp="3qs4e1clz4kif">
              <FeatureItem text="Slack-native agent in threads + mentions" data-id="element-4022" data-mp="3vk5l8k4abl1l" />
              <FeatureItem text="Persistent workspace context" data-id="element-4023" data-mp="4ee8cmfiq4wq" />
              <FeatureItem text="Integrations + tool execution" data-id="element-4024" data-mp="4durl91ltmka" />
              <FeatureItem text="Scheduled tasks & crons (reports, audits, proactive check-ins)" data-id="element-4025" data-mp="8gr2seotege7" />
              <FeatureItem text="Drafts + artifacts (updates, tickets/docs where supported)" data-id="element-4026" data-mp="deascaqyqflg" />
            </ul>
          </div>

          {/* Enterprise Card */}
          <div className="bg-[#2B211A] rounded-[2rem] p-8 md:p-10 shadow-[0_8px_40px_-12px_rgba(0,0,0,0.2)] flex flex-col relative overflow-hidden z-10" data-id="element-4027" data-mp="1xuqp0kljg5gy">
            {/* Subtle dark grid texture for the enterprise card */}
            <div className="dark-grid opacity-50" aria-hidden="true" data-id="element-4028" data-mp="1jxvcgi62vvw5" />

            <div className="relative z-10 mb-8" data-id="element-4029" data-mp="1jzom86bzmw8d">
              <span className="text-[#EA580C] font-semibold text-sm mb-4 block" data-id="element-4030" data-mp="2gmjh8css9mnm">
                Enterprise
              </span>
              <div className="flex items-baseline gap-1 mb-6" data-id="element-4031" data-mp="2cmsseemthglq">
                <span className="text-5xl md:text-6xl font-title font-bold text-white" data-id="element-4032" data-mp="27u94oouf1537">
                  Custom
                </span>
              </div>

              <div className="space-y-2" data-id="element-4033" data-mp="22ud9nf3p4nzs">
                <label className="text-sm text-white/60 font-medium block" data-id="element-4034" data-mp="30cn5mxb1t76i">
                  Flexible pricing
                </label>
                <Link to="/contact" className="w-full flex justify-center bg-white hover:bg-neutral-100 text-[#2B211A] py-4 rounded-xl font-bold transition-colors" data-id="element-4035" data-mp="2vjka4jmpnnno">
                  
                  Contact sales
                </Link>
              </div>
            </div>

            <div className="relative z-10 flex-1 mt-2" data-id="element-4036" data-mp="1dwromeioiy0g">
              <p className="text-white/80 text-sm font-medium mb-5" data-id="element-4037" data-mp="19umznt36epk5">
                Everything in Team, plus:
              </p>
              <ul className="space-y-4" data-id="element-4038" data-mp="1nt4j3em1tfw9">
                <FeatureItem text="Invoicing + custom billing terms" dark data-id="element-4039" data-mp="1ns34j05tqb9s" />
                <FeatureItem text="Security review support + DPA" dark data-id="element-4040" data-mp="1iujsm6wlmal0" />
                <FeatureItem text="SLA + priority support" dark data-id="element-4041" data-mp="1xk8xod7tayqk" />
                <FeatureItem text="Dedicated onboarding + tailored limits/controls" dark data-id="element-4042" data-mp="1skwatsgxxrnz" />
                
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>;
}